import UserService from "@/services/user-service";
import _ from "lodash";

export type TimelineStep = {
    name?: string,
    name_params?: any,
    text?: string,
    text_params?: any,
    text_type?: any,
    size: StepSize,
    comment?: string,
    completed_at?: string,
    status?: EntryStatus | null,
    lb_user_id?: number,
    type?: StepType
    entry_no?: number
}

export enum EntryStatus {
    approved = "approved",
    declined = "declined",
    reassigned = "reassigned",
    comment = "comment",
    upload = "upload",
    reassignment = "reassignment",
    skip = "skip",
    repeat = "repeat",
    system = "system",
    auditLog = "auditLog",
    canceled = "canceled"
}

export enum StepSize {
    "size-s" = "size-s",
    "size-m" = "size-m",
    "size-l" = "size-l"
}

export enum StepType {
    //Procurement
    start = "start",
    selectProduct = "select-product",
    approval = "approval",
    clearing = "clearing",
    sendOrder = "send-order",
    orderSentInternal = "order-sent-internal",
    orderSentPunchOut = "order-sent-punchout",
    delivery = "delivery",
    comment = "comment",
    reassignment = "reassignment",
    productReceived = "product-received",
    cancellation = "cancellation",
    procurementRecognition = "procurement-recognition",
    procurementCounterpartyRecognition = "procurement-counterparty-recognition",
    procurementMasterDataAdopted = "procurement-master-data-adopted",
    procurementHistoricalImport = "procurement-historical-import",


    //Invoice
    upload = "invoice-upload",
    recognition = "invoice-recognition",
    manualCheck = "invoice-manual-check",
    financialApproval = "invoice-financial-approval",
    invoiceClearing = "invoice-clearing",
    accounting = "invoice-accounting",
    invoiceVerified = "invoice-verified",
    invoiceExport = "invoice-export",
    invoiceCounterpartyRecognition = "invoice-counterparty-recognition",
    invoiceMasterDataAdopted = "invoice-master-data-adopted",
    invoiceHistoricalImport = "invoice-historical-import",
    skip = "skip",


    //Contract
    contractStart = "contract-start",
    contractRecognition = "contract-recognition",
    contractCheck = "contract-check",
    contractApproval = "contract-approval",
    contractSigning = "contract-signing",
    contractClearing = "contract-clearing"

}

const INVOICE_STEPS_BEFORE_EXPORT = [
    StepType.recognition,
    StepType.manualCheck,
    StepType.financialApproval,
    StepType.invoiceClearing,
    StepType.accounting
]

const INVOICE_EDITABLE_STEP_TYPES = [...INVOICE_STEPS_BEFORE_EXPORT, StepType.invoiceExport]

const WORKFLOW_STEP_TYPES_BY_BCASE_TYPE: Record<string, StepType[]> = {
    procurement: [
        StepType.start,
        StepType.selectProduct,
        StepType.approval,
        StepType.clearing,
        StepType.sendOrder,
        StepType.delivery
    ],
    invoice: INVOICE_EDITABLE_STEP_TYPES,
    "outgoing-invoice": [
        StepType.recognition,
        StepType.manualCheck,
        StepType.invoiceClearing,
        StepType.accounting,
        StepType.invoiceVerified
    ],
    contract: [
        StepType.contractStart,
        StepType.contractRecognition,
        StepType.contractCheck,
        StepType.contractSigning,
        StepType.contractClearing
    ]
}

export function getWorkflowStepTypesForBcaseType(bcaseType?: string): StepType[] {
    return [...(WORKFLOW_STEP_TYPES_BY_BCASE_TYPE[bcaseType ?? ""] ?? [])]
}

export function normalizeVisibleStepTypes(bcaseType: string | undefined, values: unknown): StepType[] {
    const allowed = new Set(getWorkflowStepTypesForBcaseType(bcaseType))
    return Array.from(new Set(
        (Array.isArray(values) ? values : [])
            .filter((value: unknown): value is StepType => typeof value === "string" && allowed.has(value as StepType))
    ))
}

export function isVisibleInWorkflowStep(visibleStepTypes: unknown, currentStepType?: string): boolean {
    const configuredSteps = Array.isArray(visibleStepTypes) ? visibleStepTypes : []
    return configuredSteps.length === 0 || (!!currentStepType && configuredSteps.includes(currentStepType))
}

const stepsWithoutDefaultAssignee = [
    StepType.invoiceExport,
    StepType.invoiceVerified
]

function keepsProvidedAssignee(type?: StepType) {
    return type ? stepsWithoutDefaultAssignee.includes(type) : false
}

export type TimelineSteps = Array<TimelineStep>

class TimelineServiceImpl {

    newTimeline() {
        return new TimelineBuilder()
    }

    extendTimeline(timeline: any) {
        return new TimelineBuilder(timeline)
    }

    createInsertBeforeCurrent(timeline: any, entries: Array<TimelineStep>) {
        const currentEntryNo = timeline.current_entry_no;
        const timelineId = timeline.id;
        const lbUserId = UserService.getCurrentUser()?.jwtUser?.userId;

        let no = 0
        for (const entry of entries as Array<any>) {
            entry.lb_user_id = lbUserId
            entry.timeline_id = timelineId
            entry.entry_no = currentEntryNo + (no++)
            entry.status = EntryStatus[entry.status as EntryStatus]
            entry.size = StepSize[entry.size as StepSize]
        }

        return {
            timeline_id__J9vIt: timelineId,
            currentStep__DygRb: currentEntryNo,
            newEntryCount__5Y1OB: entries.length,
            entries__vMgIg: entries
        };
    }

    createInsertAfterCurrent(timeline: any, entries: Array<TimelineStep>, afterNo?: number) {
        const nextEntryNo = (afterNo ?? timeline.current_entry_no) + 1;
        const timelineId = timeline.id;
        const lbUserId = UserService.getCurrentUser()?.jwtUser?.userId;

        let no = 0
        for (const entry of entries as Array<any>) {
            if (!keepsProvidedAssignee(entry.type as StepType))
                entry.lb_user_id = entry.lb_user_id ?? lbUserId
            entry.timeline_id = timelineId
            entry.entry_no = nextEntryNo + (no++)
            entry.status = EntryStatus[entry.status as EntryStatus]
            entry.size = StepSize[entry.size as StepSize]
        }

        return {
            timelineId: timelineId,
            nextEntryNo: nextEntryNo,
            countNewEntries: entries.length,
            entries: entries
        };
    }

    createTimeline(entries: any) {
        return {
            data: {
                timeline_entries: {
                    data: entries
                }
            }
        }
    }

    getTimelineEntryStatus(
        timeline: { current_entry_no: number },
        timelineEntry: { entry_no: number, status: string }
    ) {
        let entryNo = timelineEntry.entry_no;
        if (entryNo == 0 && timelineEntry.status != EntryStatus.system) {
            return TimelineEntryStatus.START
        }

        const currentEntryNo = timeline.current_entry_no

        if (entryNo == currentEntryNo) {
            return TimelineEntryStatus.WIP
        }

        if (entryNo > currentEntryNo) {
            return TimelineEntryStatus.FUTURE
        }

        return TimelineEntryStatus.DONE
    }

}

export enum TimelineEntryStatus {
    /**
     * The very first node of the timeline, just a short description that
     * this timeline ist
     */
    START,

    /**
     * Nodes that are done have been processed by a user. They might be
     * - approved or
     * - declined
     */
    DONE,

    /**
     * The current entry where a user must decide for approval or decline
     */
    WIP,

    /**
     * Entries that will be processed in the future. Just a hint for the user
     * what steps might come next
     */
    FUTURE,
}

class TimelineBuilder {

    private nextEntryNo = 0
    private steps: Array<any> = []

    constructor(
        private timeline: any | null = null
    ) {
        if (timeline) {
            // find the current max no and set this for the builder
            const entries = timeline.timeline_entries as Array<any>
            const maxEntryNo = _.maxBy(entries, it => it.entry_no).entry_no
            this.nextEntryNo = maxEntryNo + 1
        }
    }

    step(step: TimelineStep) {
        this.steps.push({
            ...step,
            timeline_id: this.timeline?.id,
            lb_user_id: keepsProvidedAssignee(step.type) ? step.lb_user_id : (step.lb_user_id || UserService.getCurrentUser()?.jwtUser.userId),
            entry_no: this.nextEntryNo++,
            size: "size-m"
        })
        return this
    }

    addSteps(steps: Array<TimelineStep>) {
        steps.forEach((step: TimelineStep) => this.step(step))
        return this
    }

    build(): TimelineSteps {
        return this.steps as TimelineSteps
    }
}

const TimelineService = new TimelineServiceImpl()
export default TimelineService
